/**
 * Cliente Service - Backward-Compatible Facade
 *
 * This file re-exports all types and composes the two sub-services
 * into a single default export so existing imports continue to work.
 *
 * Modules:
 *   clienteCoreService     - CRUD, format helpers, produtos, SMTP, export, Epic 46
 *   clienteHospitalService - hospital linking (Epic 26)
 *
 * For new code prefer importing the sub-services directly:
 *   import clienteCoreService from './cliente/clienteCoreService'
 *   import clienteHospitalService from './cliente/clienteHospitalService'
 */
import type { ClienteHospital, VincularHospitalRequest, DesvincularHospitalRequest } from '../types/clienteHospital';

// Re-export all types for backward compatibility
export type {
  Cliente,
  SMTPTestRequest,
  SMTPTestResponse,
  UltimaContagem,
  ViolacaoIntervalo,
  ClienteIntervaloInfo,
  ValidarIntervaloResponse,
  ValidarIntervaloMultiplosResponse,
  ClienteInput,
  ProdutoCliente,
  ProdutoClienteInput,
  ProdutoClienteGlobal,
} from './cliente/clienteTypes';

// Re-export external types that were previously re-exported
export type { ClienteHospital, VincularHospitalRequest, DesvincularHospitalRequest };

import clienteCoreService from './cliente/clienteCoreService';
import clienteHospitalService from './cliente/clienteHospitalService';

// Compose sub-services into a single object matching the original ClienteService API
const clienteService = {
  // ----- Core CRUD -----
  listClientes: clienteCoreService.listClientes.bind(clienteCoreService),
  getCliente: clienteCoreService.getCliente.bind(clienteCoreService),
  createCliente: clienteCoreService.createCliente.bind(clienteCoreService),
  updateCliente: clienteCoreService.updateCliente.bind(clienteCoreService),
  deleteCliente: clienteCoreService.deleteCliente.bind(clienteCoreService),
  reactivateCliente: clienteCoreService.reactivateCliente.bind(clienteCoreService),

  // ----- Format Helpers -----
  validateCNPJ: clienteCoreService.validateCNPJ.bind(clienteCoreService),
  formatCNPJ: clienteCoreService.formatCNPJ.bind(clienteCoreService),
  formatTelefone: clienteCoreService.formatTelefone.bind(clienteCoreService),

  // ----- Produtos do Cliente -----
  listProdutosCliente: clienteCoreService.listProdutosCliente.bind(clienteCoreService),
  createProdutoCliente: clienteCoreService.createProdutoCliente.bind(clienteCoreService),
  updateProdutoCliente: clienteCoreService.updateProdutoCliente.bind(clienteCoreService),
  deleteProdutoCliente: clienteCoreService.deleteProdutoCliente.bind(clienteCoreService),
  listAllProdutosCliente: clienteCoreService.listAllProdutosCliente.bind(clienteCoreService),

  // ----- Export & SMTP -----
  exportExcel: clienteCoreService.exportExcel.bind(clienteCoreService),
  testSMTP: clienteCoreService.testSMTP.bind(clienteCoreService),

  // ----- Epic 46: Validação Intervalo -----
  validarIntervaloAgendamento: clienteCoreService.validarIntervaloAgendamento.bind(clienteCoreService),
  validarIntervaloMultiplosClientes: clienteCoreService.validarIntervaloMultiplosClientes.bind(clienteCoreService),

  // ----- Hospitais do Cliente (Epic 26) -----
  getClienteHospitais: clienteHospitalService.getClienteHospitais.bind(clienteHospitalService),
  vincularHospitalSingle: clienteHospitalService.vincularHospitalSingle.bind(clienteHospitalService),
  vincularHospitais: clienteHospitalService.vincularHospitais.bind(clienteHospitalService),
  desvincularHospital: clienteHospitalService.desvincularHospital.bind(clienteHospitalService),
  getTodosHospitais: clienteHospitalService.getTodosHospitais.bind(clienteHospitalService),
  getNextHospitalCode: clienteHospitalService.getNextHospitalCode.bind(clienteHospitalService),
  getHospitaisDoCliente: clienteHospitalService.getHospitaisDoCliente.bind(clienteHospitalService),
  getHospitaisByCliente: clienteHospitalService.getHospitaisByCliente.bind(clienteHospitalService),
  getRepresentanteHospitais: clienteHospitalService.getRepresentanteHospitais.bind(clienteHospitalService),
  vincularRepresentanteHospitais: clienteHospitalService.vincularRepresentanteHospitais.bind(clienteHospitalService),
};

export default clienteService;
